import { motion } from "framer-motion";
import {
  Send,
  X,
  Trash2,
  PackageCheck,
  Box,
  User,
  Truck,
  Phone,
  Car,
} from "lucide-react";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  addTrackingDetail,
  fetchPartiallyDispatched,
  fetchPendingDispatch,
  updateOrderAddress,
} from "../../../store/slices/orderSlice";
import {
  findMaterialBySerialNumber,
  findProductBySerialNumber,
} from "../../../store/slices/productsSnSlice";
import Button, { ButtonType } from "../../../components/Assets/Button";
import { ItrackingDetails, TransportMode } from "../../../models/Order.model";

export interface DispatchModalProps {
  selectedOrder: any;
  isOpen: boolean;
  onClose: () => void;
}

interface MatchedItem {
  productId: string;
  sn: string;
  productName?: string;
}

type InventoryItem = {
  type: string;
  quantity: number;
  unit?: string;
  product?: { _id: string; productName?: string };
  rawMaterial?: {
    _id: string;
    name?: string;
    unit?: string;
    requireSerialNumber?: boolean;
  };
};

type ScannerInputState = {
  fieldKey: string;
  lastInputAt: number;
  rapidInputCount: number;
  timer: ReturnType<typeof setTimeout> | null;
};

interface PartiallyDispatchedItem {
  itemId?: string;
  productId?: string;
  materialId?: string;
  type?: string;
  name: string;
  assignedQuantity: number;
  availableQuantity: number;
  unit: string;
}

const requiresSerialNumbers = (item: InventoryItem) =>
  item.type === "material" &&
  item.rawMaterial?.unit === "piece" &&
  item.rawMaterial.requireSerialNumber === true;

const DispatchModal = ({
  isOpen,
  onClose,
  selectedOrder,
}: DispatchModalProps) => {
  const dispatch: RootDispatch = useDispatch();
  const serialInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const scanQueue = useRef<Promise<void>>(Promise.resolve());
  const pendingScanKeys = useRef<Set<string>>(new Set());
  const scannerInput = useRef<ScannerInputState>({
    fieldKey: "",
    lastInputAt: 0,
    rapidInputCount: 0,
    timer: null,
  });
  const matchedRef = useRef<MatchedItem[]>([]);
  const productSerialsRef = useRef<Record<string, string[]>>({});
  const materialSerialsRef = useRef<Record<string, string[]>>({});
  const [focusTick, setFocusTick] = useState(0);
  const [newMatched, setNewMatched] = useState<MatchedItem[]>([]);
  const [partiallyDispatchedItems, setPartiallyDispatchedItems] = useState<
    PartiallyDispatchedItem[]
  >([]);
  const [productSerialNumbers, setProductSerialNumbers] = useState<
    Record<string, string[]>
  >({});
  const [materialSerialNumbers, setMaterialSerialNumbers] = useState<
    Record<string, string[]>
  >({});
  const [serialEntryItem, setSerialEntryItem] = useState<InventoryItem | null>(
    null,
  );
  matchedRef.current = newMatched;
  productSerialsRef.current = productSerialNumbers;
  materialSerialsRef.current = materialSerialNumbers;

  const [trackingInfo, setTrackingInfo] = useState({
    trackingLink: "",
    trackingNumber: "",
    transportMode: "" as TransportMode | "",
    pickupName: "",
    transporterName: "",
    transporterMobile: "",
    vehicleNumber: "",
  });

  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressData, setAddressData] = useState({
    line1: "",
    city: "",
    state: "",
    pincode: "",
    mobile: "",
    altMobile: "",
  });

  const page = useSelector(
    (state: RootState) => state.order.paginations.pendingDispatch?.page,
  );
  const [order, setOrder] = useState(selectedOrder);

  useEffect(() => {
    if (!isOpen) {
      setNewMatched([]);
      matchedRef.current = [];
      setProductSerialNumbers({});
      productSerialsRef.current = {};
      setMaterialSerialNumbers({});
      materialSerialsRef.current = {};
      setSerialEntryItem(null);
      setPartiallyDispatchedItems([]);
      setTrackingInfo({
        trackingLink: "",
        trackingNumber: "",
        transportMode: "",
        pickupName: "",
        transporterName: "",
        transporterMobile: "",
        vehicleNumber: "",
      });
      setIsEditingAddress(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedOrder) setOrder(selectedOrder);
  }, [selectedOrder]);

  useEffect(() => {
    if (selectedOrder?.shippingAddress) {
      setAddressData({
        line1: selectedOrder.shippingAddress.line1 || "",
        city: selectedOrder.shippingAddress.city || "",
        state: selectedOrder.shippingAddress.state || "",
        pincode: selectedOrder.shippingAddress.pincode || "",
        mobile: selectedOrder.shippingAddress.mobile || "",
        altMobile: selectedOrder.shippingAddress.altMobile || "",
      });
    }
  }, [selectedOrder]);

  const removeSN = (productId: string, snToRemove: string) => {
    const nextMatched = matchedRef.current.filter(
      (matched) => matched.productId !== productId || matched.sn !== snToRemove,
    );
    matchedRef.current = nextMatched;
    setNewMatched(nextMatched);
    focusNextSerialInput();
  };

  const handleUpdateAddress = async () => {
    if (!order?._id) return;
    try {
      await dispatch(
        updateOrderAddress({
          orderId: order._id,
          shippingAddress: addressData,
        }),
      ).unwrap();
      toast.success("Address updated");
      setIsEditingAddress(false);
      setOrder((prev: any) => ({
        ...prev,
        shippingAddress: { ...addressData },
      }));
    } catch (error: any) {
      toast.error("Update failed");
    }
  };

  const dispatchedSerials = useMemo(() => {
    if (!selectedOrder?.dispatchQuantity) return [];
    return selectedOrder.dispatchQuantity.flatMap((dq: any) =>
      (dq.serialNumbers || []).map((sn: string) => ({
        productId: dq.productId,
        sn,
        productName: selectedOrder.goods?.find(
          (g: any) => String(g.product?._id) === dq.productId,
        )?.product?.productName,
      })),
    );
  }, [selectedOrder]);

  const getProductDispatchQuantity = useCallback(
    (productId: string, fallbackQuantity: number) => {
      const assignments =
        selectedOrder?.assignmentHistory?.[
          selectedOrder.assignmentHistory.length - 1
        ]?.items || [];
      const matchingAssignments = assignments.filter(
        (item: any) =>
          item.type === "product" &&
          String(item.itemId?._id || item.itemId) === productId,
      );
      return matchingAssignments.length
        ? matchingAssignments.reduce(
            (total: number, item: any) => total + Number(item.quantity || 0),
            0,
          )
        : fallbackQuantity;
    },
    [selectedOrder],
  );

  const getProductCounts = useCallback(
    (prodId: string) => {
      const item = selectedOrder?.goods?.find(
        (g: any) => String(g.product?._id || g.rawMaterial?._id) === prodId,
      );
      const totalRequired = getProductDispatchQuantity(
        prodId,
        Number(item?.quantity) || 0,
      );
      const unit = item?.unit || "";
      const alreadyCount = dispatchedSerials.filter(
        (m: any) => String(m.productId) === prodId,
      ).length;
      const currentCount = newMatched.filter(
        (m) => String(m.productId) === prodId,
      ).length;
      const manualSerialCount = (productSerialNumbers[prodId] || []).filter(
        (serialNumber) => serialNumber.trim(),
      ).length;
      const totalAdded = alreadyCount + currentCount + manualSerialCount;
      return {
        totalRequired,
        totalAdded,
        remaining: totalRequired - totalAdded,
        alreadyCount,
        unit,
      };
    },
    [
      selectedOrder,
      dispatchedSerials,
      newMatched,
      productSerialNumbers,
      getProductDispatchQuantity,
    ],
  );

  const getInventoryItemSerialNumbers = (item: any) => {
    const itemId = String(item.product?._id || item.rawMaterial?._id);
    const serialNumbers =
      item.type === "material"
        ? materialSerialNumbers[itemId] || []
        : [
            ...newMatched
              .filter((matchedItem) => matchedItem.productId === itemId)
              .map((matchedItem) => matchedItem.sn),
            ...(productSerialNumbers[itemId] || []),
          ];

    return serialNumbers
      .map((serialNumber) => serialNumber.trim())
      .filter(Boolean);
  };

  const hasSerialNumberWarning = (item: any) => {
    const itemId = String(item.product?._id || item.rawMaterial?._id);
    const serialNumbers = getInventoryItemSerialNumbers(item);
    const itemSerialNumbers = new Set<string>();

    for (const serialNumber of serialNumbers) {
      if (itemSerialNumbers.has(serialNumber)) return true;
      itemSerialNumbers.add(serialNumber);
    }

    const previouslyDispatchedSerialNumbers = new Set<string>(
      (selectedOrder?.dispatchQuantity || []).flatMap((dispatch: any) =>
        (dispatch.serialNumbers || []).map((serialNumber: string) =>
          serialNumber.trim(),
        ),
      ),
    );
    if (
      serialNumbers.some((serialNumber) =>
        previouslyDispatchedSerialNumbers.has(serialNumber),
      )
    ) {
      return true;
    }

    return (selectedOrder?.goods || []).some((otherItem: any) => {
      const otherItemId = String(
        otherItem.product?._id || otherItem.rawMaterial?._id,
      );
      if (otherItemId === itemId) return false;
      return getInventoryItemSerialNumbers(otherItem).some((serialNumber) =>
        itemSerialNumbers.has(serialNumber),
      );
    });
  };

  const getMaterialDispatchQuantity = useCallback(
    (materialId: string, fallbackQuantity: number) => {
      const assignments =
        selectedOrder?.assignmentHistory?.[
          selectedOrder.assignmentHistory.length - 1
        ]?.items || [];
      const assignedQuantity = assignments
        .filter(
          (item: any) =>
            item.type === "material" &&
            String(item.itemId?._id || item.itemId) === materialId,
        )
        .reduce(
          (total: number, item: any) => total + Number(item.quantity || 0),
          0,
        );

      return assignments.some(
        (item: any) =>
          item.type === "material" &&
          String(item.itemId?._id || item.itemId) === materialId,
      )
        ? assignedQuantity
        : fallbackQuantity;
    },
    [selectedOrder],
  );

  const getSerialInputCount = useCallback(
    (item: InventoryItem) => {
      if (item.type === "material") {
        return requiresSerialNumbers(item)
          ? Math.max(
              0,
              Math.ceil(
                getMaterialDispatchQuantity(
                  String(item.rawMaterial?._id),
                  Number(item.quantity) || 0,
                ),
              ),
            )
          : 0;
      }

      const counts = getProductCounts(String(item.product?._id));
      return Math.max(0, Math.ceil(counts.totalRequired - counts.alreadyCount));
    },
    [getMaterialDispatchQuantity, getProductCounts],
  );

  const getSerialEntryProgress = (item: InventoryItem) => {
    const itemId = String(
      item.type === "material" ? item.rawMaterial?._id : item.product?._id,
    );
    const requiredCount = getSerialInputCount(item);

    if (item.type === "material") {
      const serialNumbers = materialSerialNumbers[itemId] || [];
      const receivedCount = serialNumbers.filter((serialNumber) =>
        serialNumber.trim(),
      ).length;
      const isComplete =
        serialNumbers.length === requiredCount &&
        serialNumbers.every((serialNumber) => serialNumber.trim()) &&
        !hasSerialNumberWarning(item);
      return { itemId, requiredCount, receivedCount, isComplete };
    }

    const matchedCount = newMatched.filter(
      (matched) => matched.productId === itemId,
    ).length;
    const manualSerialNumbers = productSerialNumbers[itemId] || [];
    const expectedManualCount = Math.max(0, requiredCount - matchedCount);
    const receivedCount =
      matchedCount +
      manualSerialNumbers.filter((serialNumber) => serialNumber.trim()).length;
    const isComplete =
      receivedCount === requiredCount &&
      manualSerialNumbers.length === expectedManualCount &&
      manualSerialNumbers.every((serialNumber) => serialNumber.trim()) &&
      !hasSerialNumberWarning(item);

    return { itemId, requiredCount, receivedCount, isComplete };
  };

  useEffect(() => {
    if (!serialEntryItem) return;

    const itemId = String(
      serialEntryItem.type === "material"
        ? serialEntryItem.rawMaterial?._id
        : serialEntryItem.product?._id,
    );
    const inputCount =
      serialEntryItem.type === "material"
        ? Math.max(
            0,
            Math.ceil(
              getMaterialDispatchQuantity(
                itemId,
                Number(serialEntryItem.quantity) || 0,
              ),
            ),
          )
        : Math.max(
            0,
            Math.ceil(
              getProductDispatchQuantity(
                itemId,
                Number(serialEntryItem.quantity) || 0,
              ) -
                dispatchedSerials.filter(
                  (dispatchItem: any) =>
                    String(dispatchItem.productId) === itemId,
                ).length,
            ),
          );

    if (serialEntryItem.type === "material") {
      const serialNumbers = materialSerialsRef.current[itemId] || [];
      for (let index = 0; index < inputCount; index++) {
        if (!(serialNumbers[index] || "").trim()) {
          serialInputRefs.current[`${itemId}:${index}`]?.focus();
          return;
        }
      }
      return;
    }

    const matchedCount = matchedRef.current.filter(
      (matched) => matched.productId === itemId,
    ).length;
    const manualSerialNumbers = productSerialsRef.current[itemId] || [];
    for (let index = matchedCount; index < inputCount; index++) {
      if (!(manualSerialNumbers[index - matchedCount] || "").trim()) {
        serialInputRefs.current[`${itemId}:${index}`]?.focus();
        return;
      }
    }
  }, [
    focusTick,
    serialEntryItem,
    getProductDispatchQuantity,
    getMaterialDispatchQuantity,
    dispatchedSerials,
  ]);

  useEffect(
    () => () => {
      if (scannerInput.current.timer) {
        clearTimeout(scannerInput.current.timer);
      }
      scannerInput.current.timer = null;
      scannerInput.current.rapidInputCount = 0;
      scannerInput.current.fieldKey = "";
    },
    [serialEntryItem],
  );

  const focusNextSerialInput = () => {
    setFocusTick((tick) => tick + 1);
  };

  const processSerialScan = async (
    serialValue: string,
    item: InventoryItem,
    inputIndex: number,
  ) => {
    const serialNumber = serialValue.trim();
    if (!serialNumber) return;

    const isMaterial = item.type === "material";
    const itemId = String(
      isMaterial ? item.rawMaterial?._id : item.product?._id,
    );
    if (isMaterial) {
      const next = { ...materialSerialsRef.current };
      const serialNumbers = Array.from(
        { length: getSerialInputCount(item) },
        (_, index) => next[itemId]?.[index] || "",
      );
      serialNumbers[inputIndex] = "";
      next[itemId] = serialNumbers;
      materialSerialsRef.current = next;
      setMaterialSerialNumbers(next);
    } else {
      const matchedCount = matchedRef.current.filter(
        (matched) => matched.productId === itemId,
      ).length;
      const manualIndex = inputIndex - matchedCount;
      if (manualIndex >= 0) {
        const next = { ...productSerialsRef.current };
        const serialNumbers = [...(next[itemId] || [])];
        serialNumbers[manualIndex] = "";
        next[itemId] = serialNumbers;
        productSerialsRef.current = next;
        setProductSerialNumbers(next);
      }
    }

    const dispatchedSerialNumbers = [
      ...(selectedOrder?.dispatchQuantity || []).flatMap(
        (dispatchItem: any) => dispatchItem.serialNumbers || [],
      ),
      ...(selectedOrder?.dispatchHistory || []).flatMap((history: any) => [
        ...(history.serialNumbers || []).map((entry: any) =>
          typeof entry === "string" ? entry : entry.sn,
        ),
        ...Object.values(history.manualSerialNumbersByProduct || {}).flat(),
        ...Object.values(history.manualSerialNumbersByMaterial || {}).flat(),
      ]),
    ];
    const existingSerialNumbers = [
      ...matchedRef.current.map((matched) => matched.sn),
      ...Object.values(productSerialsRef.current).flat(),
      ...Object.values(materialSerialsRef.current).flat(),
      ...dispatchedSerialNumbers,
    ];
    if (
      existingSerialNumbers.some(
        (existing) =>
          typeof existing === "string" && existing.trim() === serialNumber,
      )
    ) {
      toast.error("Serial Number already added or dispatched");
      focusNextSerialInput();
      return;
    }

    if (isMaterial) {
      const result = await dispatch(findMaterialBySerialNumber(serialNumber));
      if (!findMaterialBySerialNumber.fulfilled.match(result)) {
        toast.error(
          typeof result.payload === "string"
            ? result.payload
            : "Material Serial Number is invalid",
        );
        focusNextSerialInput();
        return;
      }

      const next = { ...materialSerialsRef.current };
      const serialNumbers = Array.from(
        { length: getSerialInputCount(item) },
        (_, index) => next[itemId]?.[index] || "",
      );
      if (serialNumbers.every((value) => value.trim())) {
        toast.error("Quantity full");
        focusNextSerialInput();
        return;
      }
      serialNumbers[inputIndex] = serialNumber;
      next[itemId] = serialNumbers;
      materialSerialsRef.current = next;
      setMaterialSerialNumbers(next);
      toast.success(`Verified ${serialNumber}`);
      focusNextSerialInput();
      return;
    }

    const result = await dispatch(findProductBySerialNumber(serialNumber));
    if (!findProductBySerialNumber.fulfilled.match(result)) {
      toast.error(
        typeof result.payload === "string"
          ? result.payload
          : "Product Serial Number is invalid",
      );
      focusNextSerialInput();
      return;
    }

    const { productId, productName } = result.payload;
    const targetItem = (selectedOrder?.goods || []).find(
      (candidate: any) =>
        candidate.type !== "material" &&
        String(candidate.product?._id) === String(productId),
    );
    if (!targetItem) {
      toast.error("This product is not included in the order");
      focusNextSerialInput();
      return;
    }

    const alreadyDispatchedCount = dispatchedSerials.filter(
      (matched: any) => String(matched.productId) === String(productId),
    ).length;
    const matchedCount = matchedRef.current.filter(
      (matched) => matched.productId === String(productId),
    ).length;
    const manualCount = (
      productSerialsRef.current[String(productId)] || []
    ).filter((value) => value.trim()).length;
    if (
      getProductDispatchQuantity(
        String(productId),
        Number(targetItem.quantity) || 0,
      ) -
        alreadyDispatchedCount -
        matchedCount -
        manualCount <=
      0
    ) {
      toast.error("Quantity full");
      focusNextSerialInput();
      return;
    }

    if (itemId === String(productId)) {
      const nextProductSerials = { ...productSerialsRef.current };
      const remainingManualSerials = (
        nextProductSerials[String(productId)] || []
      )
        .filter((value) => value.trim())
        .slice(0, Math.max(0, getSerialInputCount(item) - matchedCount - 1));
      nextProductSerials[String(productId)] = remainingManualSerials;
      productSerialsRef.current = nextProductSerials;
      setProductSerialNumbers(nextProductSerials);
    }

    const nextMatched = [
      ...matchedRef.current,
      { productId: String(productId), sn: serialNumber, productName },
    ];
    matchedRef.current = nextMatched;
    setNewMatched(nextMatched);
    toast.success(`Verified ${serialNumber}`);
    focusNextSerialInput();
  };

  const queueSerialScan = (
    serialValue: string,
    item: InventoryItem,
    inputIndex: number,
  ) => {
    const serialNumber = serialValue.trim();
    if (!serialNumber) return;

    const itemId = String(
      item.type === "material" ? item.rawMaterial?._id : item.product?._id,
    );
    const scanKey = `${itemId}:${inputIndex}:${serialNumber}`;
    if (pendingScanKeys.current.has(scanKey)) return;
    pendingScanKeys.current.add(scanKey);

    scanQueue.current = scanQueue.current
      .then(() => processSerialScan(serialNumber, item, inputIndex))
      .catch((error) => {
        console.error("Failed to validate scanned serial number", error);
        toast.error("Unable to validate serial number");
        focusNextSerialInput();
      })
      .finally(() => {
        pendingScanKeys.current.delete(scanKey);
      });
  };

  const handleSerialKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    item: InventoryItem,
    inputIndex: number,
  ) => {
    const itemId = String(
      item.type === "material" ? item.rawMaterial?._id : item.product?._id,
    );
    const fieldKey = `${itemId}:${inputIndex}`;
    const typing = scannerInput.current;
    const isScannerTab =
      event.key === "Tab" &&
      typing.fieldKey === fieldKey &&
      typing.rapidInputCount >= 3;

    if (event.key !== "Enter" && !isScannerTab) return;
    event.preventDefault();
    if (typing.timer) clearTimeout(typing.timer);
    typing.timer = null;
    typing.rapidInputCount = 0;
    queueSerialScan(event.currentTarget.value, item, inputIndex);
  };

  const hasRequiredMaterialSerialNumbers = useMemo(() => {
    if (!selectedOrder?.goods) return false;
    const seenSerialNumbers = new Set<string>();
    return selectedOrder.goods.every((item: any) => {
      if (item.type !== "material" || !requiresSerialNumbers(item)) return true;
      const materialId = String(item.rawMaterial?._id);
      const requiredQuantity = getMaterialDispatchQuantity(
        materialId,
        Number(item.quantity) || 0,
      );
      const serialNumbers = materialSerialNumbers[materialId] || [];
      if (
        serialNumbers.length !== requiredQuantity ||
        !serialNumbers.every(
          (serialNumber) =>
            typeof serialNumber === "string" && serialNumber.trim(),
        )
      ) {
        return false;
      }
      for (const serialNumber of serialNumbers) {
        const normalizedSerialNumber = serialNumber.trim();
        if (seenSerialNumbers.has(normalizedSerialNumber)) return false;
        seenSerialNumbers.add(normalizedSerialNumber);
      }
      return true;
    });
  }, [selectedOrder, materialSerialNumbers, getMaterialDispatchQuantity]);

  const hasUniqueSerialNumbers = useMemo(() => {
    if (!selectedOrder) return false;
    const previouslyDispatchedSerialNumbers = new Set<string>([
      ...(selectedOrder.dispatchQuantity || []).flatMap((item: any) =>
        (item.serialNumbers || []).map((serialNumber: string) =>
          serialNumber.trim(),
        ),
      ),
      ...(selectedOrder.dispatchHistory || []).flatMap((history: any) => [
        ...(history.serialNumbers || []).map((entry: any) =>
          String(typeof entry === "string" ? entry : entry.sn).trim(),
        ),
        ...Object.values(history.manualSerialNumbersByProduct || {}).flatMap(
          (serialNumbers: any) =>
            serialNumbers.map((serialNumber: string) => serialNumber.trim()),
        ),
        ...Object.values(history.manualSerialNumbersByMaterial || {}).flatMap(
          (serialNumbers: any) =>
            serialNumbers.map((serialNumber: string) => serialNumber.trim()),
        ),
      ]),
    ]);
    const enteredSerialNumbers = new Set<string>();
    const serialNumbers = [
      ...newMatched.map((item) => item.sn),
      ...Object.values(productSerialNumbers).flat(),
      ...Object.values(materialSerialNumbers).flat(),
    ];

    return serialNumbers.every((serialNumber) => {
      const normalizedSerialNumber = serialNumber.trim();
      if (!normalizedSerialNumber) return true;
      if (
        enteredSerialNumbers.has(normalizedSerialNumber) ||
        previouslyDispatchedSerialNumbers.has(normalizedSerialNumber)
      ) {
        return false;
      }
      enteredSerialNumbers.add(normalizedSerialNumber);
      return true;
    });
  }, [selectedOrder, newMatched, productSerialNumbers, materialSerialNumbers]);

  const isOrderFullyMatched = useMemo(() => {
    if (!selectedOrder?.goods || !hasUniqueSerialNumbers) return false;

    return selectedOrder.goods.every((g: any) => {
      if (g.type === "material") return true;
      return (
        getProductCounts(String(g.product?._id)).remaining === 0 &&
        (productSerialNumbers[String(g.product?._id)] || []).every(
          (serialNumber) => serialNumber.trim(),
        )
      );
    });
  }, [
    selectedOrder,
    hasUniqueSerialNumbers,
    productSerialNumbers,
    getProductCounts,
  ]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOrderFullyMatched) return toast.error("Match all products first");
    if (!hasRequiredMaterialSerialNumbers)
      return toast.error(
        "Enter a unique serial number for each required material",
      );

    const goodsPayload = selectedOrder.goods.map((g: any) => ({
      productId: String(g.product?._id || g.rawMaterial?._id),
      quantity: g.quantity,
    }));

    const manualSerialNumbersByProduct: Record<string, string[]> = {};
    Object.entries(productSerialNumbers).forEach(
      ([productId, serialNumbers]) => {
        const enteredSerialNumbers = serialNumbers
          .map((serialNumber) => serialNumber.trim())
          .filter(Boolean);
        if (enteredSerialNumbers.length) {
          manualSerialNumbersByProduct[productId] = [
            ...(manualSerialNumbersByProduct[productId] || []),
            ...enteredSerialNumbers,
          ];
        }
      },
    );

    const payload: ItrackingDetails[] = [
      {
        productIds: selectedOrder.goods.map((g: any) =>
          String(g.product?._id || g.rawMaterial?._id),
        ),
        ...trackingInfo,
        serialNumbers: newMatched.map((m) => ({ sn: m.sn })),
        manualSerialNumbersByMaterial: Object.fromEntries(
          Object.entries(materialSerialNumbers)
            .map(([materialId, serialNumbers]) => [
              materialId,
              serialNumbers.map((serialNumber) => serialNumber.trim()),
            ])
            .filter(([, serialNumbers]) =>
              (serialNumbers as string[]).some(Boolean),
            ),
        ),
        manualSerialNumbersByProduct,
      } as any,
    ];

    try {
      const res = await dispatch(
        addTrackingDetail({
          orderId: selectedOrder._id,
          goods: goodsPayload,
          trackingDetails: payload,
        }),
      ).unwrap();
      if (res.success) {
        await Promise.all([
          dispatch(fetchPendingDispatch({ page: page ?? 1 })),
          dispatch(fetchPartiallyDispatched({ page: page ?? 1 })),
        ]);
        const partialItems = res.data?.partiallyDispatchedItems;
        const partialProducts = res.data?.partiallyDispatchedProducts;
        const partialMaterials = res.data?.partiallyDispatchedMaterials;
        const itemsToDisplay =
          Array.isArray(partialItems) && partialItems.length > 0
            ? partialItems
            : [
                ...(Array.isArray(partialProducts) ? partialProducts : []),
                ...(Array.isArray(partialMaterials) ? partialMaterials : []),
              ];
        if (itemsToDisplay.length > 0) {
          setPartiallyDispatchedItems(itemsToDisplay);
        } else {
          onClose();
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const serialEntryProgress = serialEntryItem
    ? getSerialEntryProgress(serialEntryItem)
    : null;
  const serialEntryItemId = serialEntryProgress?.itemId || "";
  const serialEntryIsMaterial = serialEntryItem?.type === "material";
  const serialEntryMatchedSerialNumbers = serialEntryItem
    ? newMatched.filter((matched) => matched.productId === serialEntryItemId)
    : [];
  const serialEntryManualSerialNumbers = serialEntryItem
    ? productSerialNumbers[serialEntryItemId] || []
    : [];

  if (!isOpen || !selectedOrder || !selectedOrder.orderNumber) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-[110] backdrop-blur-sm flex justify-end">
      <motion.div
        className="h-full w-full max-w-lg bg-white shadow-2xl flex flex-col"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-lg text-white">
              <PackageCheck size={20} />
            </div>
            <h3 className="text-lg font-bold text-slate-800">
              Dispatch Order #{selectedOrder?.orderNumber}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          <section className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h4 className="text-xs font-bold uppercase text-slate-400">
                Inventory Items
              </h4>
              {selectedOrder.goods?.some(
                (item: any) =>
                  item.type !== "material" ||
                  requiresSerialNumbers(item as InventoryItem),
              ) && (
                <span className="self-start rounded-md bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700 sm:self-auto">
                  Click an item to enter serial numbers
                </span>
              )}
            </div>
            <div className="grid gap-2">
              {selectedOrder?.goods?.map((g: any, i: number) => {
                const item = g as InventoryItem;
                const serialRequired =
                  g.type !== "material" || requiresSerialNumbers(item);
                const progress = serialRequired
                  ? getSerialEntryProgress(item)
                  : null;
                const isDone = progress?.isComplete ?? true;
                const hasSerialWarning = Boolean(
                  serialRequired && hasSerialNumberWarning(g),
                );
                const statusMessage = isDone
                  ? "Received"
                  : hasSerialWarning
                    ? "Check serials"
                    : "Incomplete";
                const enteredSerialNumbers = serialRequired
                  ? g.type === "material"
                    ? materialSerialNumbers[progress?.itemId || ""] || []
                    : [
                        ...newMatched
                          .filter(
                            (matched) => matched.productId === progress?.itemId,
                          )
                          .map((matched) => matched.sn),
                        ...(productSerialNumbers[progress?.itemId || ""] || []),
                      ]
                  : [];
                const itemName =
                  g.product?.productName ||
                  g.rawMaterial?.name ||
                  "Inventory item";
                return (
                  <div
                    key={i}
                    role={serialRequired ? "button" : undefined}
                    tabIndex={serialRequired ? 0 : undefined}
                    aria-label={
                      serialRequired
                        ? `${itemName}, ${statusMessage}`
                        : undefined
                    }
                    aria-invalid={Boolean(serialRequired && !isDone)}
                    aria-expanded={serialRequired && serialEntryItem === item}
                    onClick={() => {
                      if (serialRequired) setSerialEntryItem(item);
                    }}
                    onKeyDown={(event) => {
                      if (
                        serialRequired &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        setSerialEntryItem(item);
                      }
                    }}
                    className={`flex flex-col gap-3 p-3 rounded-xl border transition-colors ${
                      isDone
                        ? "bg-green-50 border-green-200"
                        : serialRequired
                          ? "bg-red-50 border-red-300 shadow-sm"
                          : "bg-white border-slate-200 shadow-sm"
                    } ${
                      serialRequired
                        ? "cursor-pointer hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        : ""
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">
                          {itemName}
                        </span>
                        <span className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
                          <Box size={10} />{" "}
                          {g.type === "material" ? "Material" : "Product"}
                        </span>
                      </div>
                      <span
                        className={`text-xs px-3 py-1 rounded-full font-bold ${
                          isDone
                            ? "bg-green-600 text-white"
                            : serialRequired
                              ? "bg-red-100 text-red-700"
                              : "bg-indigo-100 text-indigo-700"
                        }`}
                      >
                        {progress ? statusMessage : `${g.quantity} ${g.unit}`}
                      </span>
                    </div>
                    {enteredSerialNumbers.some((serialNumber) =>
                      serialNumber.trim(),
                    ) && (
                      <div className="border-t border-slate-200 pt-2">
                        <p className="mb-1 text-[10px] font-semibold uppercase text-slate-500">
                          Entered serial numbers
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {enteredSerialNumbers
                            .map((serialNumber) => serialNumber.trim())
                            .filter(Boolean)
                            .map((serialNumber, serialIndex) => (
                              <span
                                key={`${serialNumber}-${serialIndex}`}
                                className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-xs text-slate-700"
                              >
                                {serialNumber}
                              </span>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <div className="space-y-4 pt-4 border-t">
            <div className="space-y-3 bg-white p-1 rounded-xl">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase ml-1">
                  Transport Mode
                </label>
                <select
                  value={trackingInfo.transportMode}
                  onChange={(e) =>
                    setTrackingInfo({
                      ...trackingInfo,
                      transportMode: e.target.value as any,
                    })
                  }
                  className="w-full text-sm p-2.5 border rounded-xl mt-1"
                >
                  <option value="">Select Mode</option>
                  <option value="Road">Road</option>
                  <option value="Courier">Courier</option>
                  <option value="Self Pick-up">Self Pick-up</option>
                  <option value="Air">Air</option>
                  <option value="Rail">Rail</option>
                  <option value="Sea">Sea</option>
                </select>
              </div>

              {trackingInfo.transportMode === "Road" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                      <Truck size={10} /> Transporter Name
                    </label>
                    <input
                      type="text"
                      value={trackingInfo.transporterName}
                      onChange={(e) =>
                        setTrackingInfo({
                          ...trackingInfo,
                          transporterName: e.target.value,
                        })
                      }
                      className="w-full text-sm p-2 border rounded-lg mt-1"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                      <Phone size={10} /> Mobile
                    </label>
                    <input
                      type="text"
                      value={trackingInfo.transporterMobile}
                      onChange={(e) =>
                        setTrackingInfo({
                          ...trackingInfo,
                          transporterMobile: e.target.value,
                        })
                      }
                      className="w-full text-sm p-2 border rounded-lg mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                      <Car size={10} /> Vehicle Number
                    </label>
                    <input
                      type="text"
                      value={trackingInfo.vehicleNumber}
                      onChange={(e) =>
                        setTrackingInfo({
                          ...trackingInfo,
                          vehicleNumber: e.target.value,
                        })
                      }
                      className="w-full text-sm p-2 border rounded-lg mt-1"
                    />
                  </div>
                </div>
              )}

              {trackingInfo.transportMode === "Self Pick-up" && (
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
                    <User size={10} /> Pick-up Person Name
                  </label>
                  <input
                    type="text"
                    value={trackingInfo.pickupName}
                    onChange={(e) =>
                      setTrackingInfo({
                        ...trackingInfo,
                        pickupName: e.target.value,
                      })
                    }
                    className="w-full text-sm p-2 border rounded-lg mt-1"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase ml-1">
                    Tracking Number
                  </label>
                  <input
                    type="text"
                    value={trackingInfo.trackingNumber}
                    onChange={(e) =>
                      setTrackingInfo({
                        ...trackingInfo,
                        trackingNumber: e.target.value,
                      })
                    }
                    className="w-full text-sm p-2.5 border rounded-xl mt-1"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase ml-1">
                    Tracking Link
                  </label>
                  <input
                    type="url"
                    value={trackingInfo.trackingLink}
                    onChange={(e) =>
                      setTrackingInfo({
                        ...trackingInfo,
                        trackingLink: e.target.value,
                      })
                    }
                    className="w-full text-sm p-2.5 border rounded-xl mt-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold uppercase text-slate-400">
                Shipping Address
              </h4>
              <button
                onClick={() =>
                  isEditingAddress
                    ? handleUpdateAddress()
                    : setIsEditingAddress(true)
                }
                className={`text-xs font-bold px-3 py-1 rounded-full transition-all ${isEditingAddress ? "bg-green-600 text-white" : "text-indigo-600 bg-indigo-50 hover:bg-indigo-100"}`}
              >
                {isEditingAddress ? "Save" : "Edit"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <div className="col-span-2">
                <label className="text-[9px] uppercase font-bold text-slate-400">
                  Address Line
                </label>
                {isEditingAddress ? (
                  <input
                    type="text"
                    value={addressData.line1}
                    onChange={(e) =>
                      setAddressData({ ...addressData, line1: e.target.value })
                    }
                    className="w-full text-sm p-2 border rounded-lg mt-1"
                  />
                ) : (
                  <p className="text-sm font-medium">
                    {addressData.line1 || "N/A"}
                  </p>
                )}
              </div>
              {["city", "state", "pincode", "mobile", "altMobile"].map(
                (field) => (
                  <div key={field}>
                    <label className="text-[9px] uppercase font-bold text-slate-400">
                      {field === "altMobile" ? "Alt Mobile" : field}
                    </label>
                    {isEditingAddress ? (
                      <input
                        type="text"
                        value={(addressData as any)[field]}
                        onChange={(e) =>
                          setAddressData({
                            ...addressData,
                            [field]: e.target.value,
                          })
                        }
                        className="w-full text-sm p-2 border rounded-lg mt-1"
                      />
                    ) : (
                      <p className="text-sm font-medium">
                        {(addressData as any)[field] || "N/A"}
                      </p>
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-slate-50 flex gap-3">
          <Button
            type={ButtonType.Cancel}
            onClick={onClose}
            className="flex-1 py-3"
          >
            Cancel
          </Button>
          <button
            onClick={onSubmit}
            disabled={!isOrderFullyMatched || !hasRequiredMaterialSerialNumbers}
            className={`flex-[2] text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${isOrderFullyMatched && hasRequiredMaterialSerialNumbers ? "bg-indigo-600 hover:bg-indigo-700" : "bg-slate-400 cursor-not-allowed"}`}
          >
            <Send size={16} />{" "}
            {isOrderFullyMatched && hasRequiredMaterialSerialNumbers
              ? "Confirm Full Dispatch"
              : "Add Required Serial Numbers"}
          </button>
        </div>
      </motion.div>
      {serialEntryItem && serialEntryProgress && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="serial-entry-title"
            className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b bg-slate-50 px-5 py-4">
              <div>
                <h3
                  id="serial-entry-title"
                  className="font-semibold text-slate-800"
                >
                  {serialEntryItem.type === "material"
                    ? serialEntryItem.rawMaterial?.name
                    : serialEntryItem.product?.productName}
                </h3>
                <p className="text-xs text-slate-500">
                  {serialEntryIsMaterial ? "Raw material" : "Product"} serial
                  numbers
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSerialEntryItem(null)}
                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-200"
                aria-label="Close serial entry"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              <p className="text-sm text-slate-600">
                Enter serial numbers manually, or scan a recognized serial and
                press Enter to validate it.
              </p>
              <div className="grid gap-3">
                {Array.from({
                  length: serialEntryProgress.requiredCount,
                }).map((_, serialIndex) => {
                  if (serialEntryIsMaterial) {
                    return (
                      <label
                        key={serialIndex}
                        className="grid gap-1 text-xs font-medium text-slate-600"
                      >
                        Serial Number {serialIndex + 1}
                        <input
                          ref={(element) => {
                            serialInputRefs.current[
                              `${serialEntryItemId}:${serialIndex}`
                            ] = element;
                          }}
                          type="text"
                          value={
                            materialSerialNumbers[serialEntryItemId]?.[
                              serialIndex
                            ] || ""
                          }
                          onChange={(event) => {
                            const next = {
                              ...materialSerialsRef.current,
                            };
                            const serialNumbers = Array.from(
                              {
                                length: serialEntryProgress.requiredCount,
                              },
                              (_, index) =>
                                next[serialEntryItemId]?.[index] || "",
                            );
                            serialNumbers[serialIndex] = event.target.value;
                            next[serialEntryItemId] = serialNumbers;
                            materialSerialsRef.current = next;
                            setMaterialSerialNumbers(next);
                          }}
                          onKeyDown={(event) =>
                            handleSerialKeyDown(
                              event,
                              serialEntryItem,
                              serialIndex,
                            )
                          }
                          placeholder={`Serial Number ${serialIndex + 1}`}
                          className="w-full rounded-lg border p-3 font-mono text-sm"
                        />
                      </label>
                    );
                  }

                  const manualIndex =
                    serialIndex - serialEntryMatchedSerialNumbers.length;
                  if (manualIndex < 0) {
                    return (
                      <div
                        key={serialIndex}
                        className="grid gap-1 text-xs font-medium text-slate-600"
                      >
                        <span>Serial Number {serialIndex + 1} (verified)</span>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            readOnly
                            value={
                              serialEntryMatchedSerialNumbers[serialIndex].sn
                            }
                            className="min-w-0 flex-1 rounded-lg border border-green-200 bg-green-50 p-3 font-mono text-sm"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              removeSN(
                                serialEntryItemId,
                                serialEntryMatchedSerialNumbers[serialIndex].sn,
                              )
                            }
                            className="flex items-center gap-1 rounded-lg border border-red-200 px-3 text-red-600 transition-colors hover:bg-red-50"
                            aria-label={`Clear verified serial number ${serialEntryMatchedSerialNumbers[serialIndex].sn}`}
                          >
                            <Trash2 size={16} />
                            Clear
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <label
                      key={serialIndex}
                      className="grid gap-1 text-xs font-medium text-slate-600"
                    >
                      Serial Number {serialIndex + 1}
                      <input
                        ref={(element) => {
                          serialInputRefs.current[
                            `${serialEntryItemId}:${serialIndex}`
                          ] = element;
                        }}
                        type="text"
                        value={
                          serialEntryManualSerialNumbers[manualIndex] || ""
                        }
                        onChange={(event) => {
                          const next = {
                            ...productSerialsRef.current,
                          };
                          const manualSerialCount = Math.max(
                            0,
                            serialEntryProgress.requiredCount -
                              serialEntryMatchedSerialNumbers.length,
                          );
                          const serialNumbers = Array.from(
                            { length: manualSerialCount },
                            (_, index) =>
                              next[serialEntryItemId]?.[index] || "",
                          );
                          serialNumbers[manualIndex] = event.target.value;
                          next[serialEntryItemId] = serialNumbers;
                          productSerialsRef.current = next;
                          setProductSerialNumbers(next);
                        }}
                        onKeyDown={(event) =>
                          handleSerialKeyDown(
                            event,
                            serialEntryItem,
                            serialIndex,
                          )
                        }
                        placeholder={`Serial Number ${serialIndex + 1}`}
                        className="w-full rounded-lg border p-3 font-mono text-sm"
                      />
                    </label>
                  );
                })}
              </div>
              <p
                className={`rounded-lg p-3 text-sm font-medium ${
                  serialEntryProgress.isComplete
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-700"
                }`}
                role={serialEntryProgress.isComplete ? "status" : "alert"}
              >
                {serialEntryProgress.isComplete
                  ? "Serials received"
                  : hasSerialNumberWarning(serialEntryItem)
                    ? "Check serials"
                    : "Incomplete"}
              </p>
            </div>

            <div className="flex justify-end gap-3 border-t bg-slate-50 p-4">
              <button
                type="button"
                onClick={() => setSerialEntryItem(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white"
              >
                Close
              </button>
              <button
                type="button"
                disabled={!serialEntryProgress.isComplete}
                onClick={() => setSerialEntryItem(null)}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {partiallyDispatchedItems.length > 0 && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="partial-dispatch-title"
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="border-b bg-slate-50 px-6 py-5">
              <h2
                id="partial-dispatch-title"
                className="text-lg font-semibold text-slate-800"
              >
                Dispatch partially completed
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                The following items were not dispatched because of low stock.
                Other eligible items may have been dispatched successfully.
              </p>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto p-6">
              {partiallyDispatchedItems.map((item, index) => (
                <div
                  key={`${item.itemId ?? item.productId ?? item.materialId ?? item.name}-${index}`}
                  className="rounded-xl border border-amber-200 bg-amber-50/60 p-4"
                >
                  <h3 className="font-semibold text-slate-800">{item.name}</h3>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
                    <div>
                      <dt className="text-xs text-slate-500">
                        Assigned quantity
                      </dt>
                      <dd className="mt-1 font-medium text-slate-800">
                        {item.assignedQuantity} {item.unit}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">
                        Available quantity
                      </dt>
                      <dd className="mt-1 font-medium text-slate-800">
                        {item.availableQuantity} {item.unit}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Shortfall</dt>
                      <dd className="mt-1 font-semibold text-amber-800">
                        {item.assignedQuantity - item.availableQuantity}{" "}
                        {item.unit}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-slate-500">Unit</dt>
                      <dd className="mt-1 font-medium text-slate-800">
                        {item.unit}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>

            <div className="flex justify-end border-t bg-slate-50 p-4">
              <button
                type="button"
                onClick={() => {
                  setPartiallyDispatchedItems([]);
                  onClose();
                }}
                className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchModal;
